import { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import api from "../../lib/api";
import PageHeader from "../../components/PageHeader";
import { Button } from "../../components/ui/button";
import CrudModal from "../../components/CrudModal";
import { toast } from "sonner";
import { FileText, Sparkle, Download, PencilSimple, Trash, Plus, ShieldCheck, ChalkboardTeacher } from "@phosphor-icons/react";

const STATUS_CLS = {
  Ready: "bg-emerald-50 text-emerald-700",
  "In Review": "bg-amber-50 text-amber-800",
  Draft: "bg-secondary text-muted-foreground",
};

const buildFields = (isEdit) => [
  { name: "code", label: "Code", type: "text", hint: isEdit ? "Code cannot be changed" : "e.g. NIRF-2026" },
  { name: "name", label: "Report name", type: "text" },
  { name: "due", label: "Due date (YYYY-MM-DD)", type: "text" },
  { name: "auto_pct", label: "Auto-populated %", type: "number" },
  { name: "status", label: "Status", type: "select", options: [
    { value: "Ready", label: "Ready" }, { value: "In Review", label: "In Review" }, { value: "Draft", label: "Draft" },
  ]},
];

export default function Reports() {
  const { t } = useTranslation();
  const [tab, setTab] = useState("reports");
  const [rows, setRows] = useState([]);
  const [modal, setModal] = useState({ open: false, editing: null });
  const [faculty, setFaculty] = useState([]);
  const [facForm, setFacForm] = useState({ name: "", email: "", department: "", designation: "", teaching_load: 12 });
  const [resolutions, setResolutions] = useState([]);
  const [resForm, setResForm] = useState({ title: "", body: "", council: "Academic Council", effective_from: "" });
  const [aggReport, setAggReport] = useState(null);
  const [aggCode, setAggCode] = useState("NIRF");

  const load = useCallback(async () => {
    const [r, f, res] = await Promise.all([
      api.get("/compliance/reports"), api.get("/faculty"), api.get("/governance/resolutions"),
    ]);
    setRows(r.data); setFaculty(f.data); setResolutions(res.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const saveFaculty = async () => {
    if (!facForm.name || !facForm.email) return toast.error("Name & email required");
    try { await api.post("/faculty", { ...facForm, teaching_load: Number(facForm.teaching_load) });
      toast.success("Faculty added");
      setFacForm({ name: "", email: "", department: "", designation: "", teaching_load: 12 }); load();
    } catch { toast.error("Failed"); }
  };

  const saveResolution = async () => {
    if (!resForm.title || !resForm.body) return toast.error("Title & body required");
    try { await api.post("/governance/resolutions", { ...resForm, effective_from: resForm.effective_from || null });
      toast.success("Resolution recorded");
      setResForm({ title: "", body: "", council: "Academic Council", effective_from: "" }); load();
    } catch { toast.error("Failed"); }
  };

  const aggregateReport = async () => {
    try {
      const { data } = await api.get(`/compliance/aggregate/${aggCode}`);
      setAggReport(data);
      toast.success(`${aggCode} report aggregated`);
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const openCreate = () => setModal({ open: true, editing: null });
  const openEdit = (r) => setModal({ open: true, editing: r });
  const close = () => setModal({ open: false, editing: null });

  const save = async (values) => {
    try {
      if (modal.editing) {
        const { code, ...patch } = values;
        await api.patch(`/compliance/reports/${modal.editing.code}`, patch);
        toast.success("Report updated");
      } else {
        await api.post("/compliance/reports", values);
        toast.success("Report added");
      }
      close(); load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save");
    }
  };

  const remove = async (r) => {
    if (!window.confirm(`Delete report ${r.code}?`)) return;
    try { await api.delete(`/compliance/reports/${r.code}`); toast.success("Deleted"); load(); }
    catch { toast.error("Failed to delete"); }
  };

  const download = async (r) => {
    try {
      const res = await api.get(`/compliance/reports/${r.code}/download`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.download = `${r.code}_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(link); link.click(); link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Report downloaded");
    } catch { toast.error("Download failed"); }
  };

  return (
    <div>
      <PageHeader
        overline="Compliance & Analytics"
        title={t("modules.reports.title")}
        description={t("modules.reports.subtitle")}
        action={
          <div className="flex items-center gap-2">
            <Button variant="outline" className="rounded-md" data-testid="ai-fill-btn"><Sparkle size={14} weight="fill" className="mr-1.5"/> AI auto-fill all</Button>
            <Button onClick={openCreate} className="rounded-md bg-[hsl(244_53%_20%)] hover:bg-[hsl(244_53%_15%)]" data-testid="new-report-btn">
              <Plus size={14} className="mr-1.5"/> New report
            </Button>
          </div>
        }
      />
      <div className="p-6 lg:p-8 space-y-6">
        <div className="inline-flex flex-wrap border border-border rounded-md overflow-hidden" data-testid="reports-tabs">
          {[
            { v: "reports", l: "Reports", I: FileText },
            { v: "faculty", l: "Faculty", I: ChalkboardTeacher },
            { v: "governance", l: "Governance", I: ShieldCheck },
            { v: "aggregate", l: "Auto-aggregate (NIRF/NAAC/AISHE)", I: Sparkle },
          ].map(it => (
            <button key={it.v} onClick={() => setTab(it.v)}
              className={`px-4 py-2 text-sm inline-flex items-center gap-1.5 border-r border-border last:border-r-0 ${tab === it.v ? "bg-[hsl(244_53%_20%)] text-white" : "bg-white hover:bg-secondary"}`}
              data-testid={`reports-tab-${it.v}`}>
              <it.I size={14}/> {it.l}
            </button>
          ))}
        </div>

        {tab === "reports" && (
        <div className="eos-card p-0 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-secondary text-muted-foreground">
              <tr>{["Code", "Report", "Due", "Auto-populated", "Status", "Actions"].map(h => (
                <th key={h} className="text-left px-4 py-2.5 text-[11px] uppercase tracking-[0.15em] font-medium">{h}</th>
              ))}</tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.code} className="border-t border-border hover:bg-secondary/40" data-testid={`report-${r.code}`}>
                  <td className="px-4 py-3 font-mono-eos text-[12px]">{r.code}</td>
                  <td className="px-4 py-3 font-medium flex items-center gap-2">
                    <FileText size={14} weight="duotone" className="text-[hsl(244_53%_20%)]"/> {r.name}
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">{r.due}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2 w-44">
                      <div className="h-1.5 flex-1 bg-border rounded-full overflow-hidden">
                        <div className="h-full bg-[hsl(20_90%_48%)]" style={{ width: `${r.auto_pct}%` }}/>
                      </div>
                      <span className="font-mono-eos text-xs">{r.auto_pct}%</span>
                    </div>
                  </td>
                  <td className="px-4 py-3"><span className={`text-[11px] px-2 py-1 rounded-md ${STATUS_CLS[r.status]}`}>{r.status}</span></td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1">
                      <Button variant="ghost" onClick={() => download(r)} className="h-7 px-2 text-xs rounded" data-testid={`export-${r.code}`}><Download size={12} className="mr-1"/> Export</Button>
                      <Button variant="ghost" onClick={() => openEdit(r)} className="h-7 w-7 p-0 rounded" title="Edit" data-testid={`edit-${r.code}`}><PencilSimple size={13}/></Button>
                      <Button variant="ghost" onClick={() => remove(r)} className="h-7 w-7 p-0 rounded text-rose-600 hover:text-rose-700 hover:bg-rose-50" title="Delete" data-testid={`delete-${r.code}`}><Trash size={13}/></Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        )}

        {tab === "faculty" && (
          <div className="space-y-4" data-testid="faculty-pane">
            <div className="eos-card">
              <div className="font-heading font-bold text-base mb-3">Add faculty</div>
              <div className="grid grid-cols-5 gap-2">
                {[["name", "Name"], ["email", "Email"], ["department", "Dept"], ["designation", "Designation"], ["teaching_load", "Load h/wk"]].map(([k, l]) => (
                  <div key={k}><label className="eos-overline">{l}</label><input value={facForm[k]} onChange={(e) => setFacForm(f => ({ ...f, [k]: e.target.value }))} className="mt-1 w-full px-2 py-2 text-sm border border-border rounded-md" data-testid={`fac-${k}`}/></div>
                ))}
              </div>
              <Button onClick={saveFaculty} className="mt-3 bg-[hsl(244_53%_20%)] hover:bg-[hsl(244_53%_15%)]" data-testid="save-fac-btn"><Plus size={14} className="mr-1.5"/> Save</Button>
            </div>
            <div className="eos-card p-0 overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-secondary text-muted-foreground"><tr>{["ID", "Name", "Email", "Dept", "Designation", "Load", "Status"].map(h => <th key={h} className="text-left px-3 py-2 text-[10px] uppercase tracking-[0.15em]">{h}</th>)}</tr></thead>
                <tbody>
                  {faculty.map(f => (
                    <tr key={f.faculty_id} className="border-t border-border" data-testid={`fac-row-${f.faculty_id}`}>
                      <td className="px-3 py-2 font-mono-eos text-[11px]">{f.faculty_id}</td>
                      <td className="px-3 py-2 font-medium">{f.name}</td>
                      <td className="px-3 py-2 text-[12px]">{f.email}</td>
                      <td className="px-3 py-2">{f.department}</td>
                      <td className="px-3 py-2 text-[12px]">{f.designation}</td>
                      <td className="px-3 py-2 font-mono-eos">{f.teaching_load}h</td>
                      <td className="px-3 py-2"><span className={`text-[10px] px-1.5 py-0.5 rounded ${f.status === "active" ? "bg-emerald-50 text-emerald-700" : "bg-secondary"}`}>{f.status}</span></td>
                    </tr>
                  ))}
                  {faculty.length === 0 && <tr><td colSpan="7" className="p-6 text-center text-sm text-muted-foreground">No faculty.</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === "governance" && (
          <div className="space-y-4" data-testid="governance-pane">
            <div className="eos-card">
              <div className="font-heading font-bold text-base mb-3">Record resolution / regulation</div>
              <div className="grid grid-cols-2 gap-2">
                <div><label className="eos-overline">Title</label><input value={resForm.title} onChange={(e) => setResForm(f => ({ ...f, title: e.target.value }))} className="mt-1 w-full px-2 py-2 text-sm border border-border rounded-md" data-testid="res-title"/></div>
                <div><label className="eos-overline">Council</label><select value={resForm.council} onChange={(e) => setResForm(f => ({ ...f, council: e.target.value }))} className="mt-1 w-full px-2 py-2 text-sm border border-border rounded-md bg-white" data-testid="res-council">{["Academic Council", "BoS", "Senate", "Governing Body"].map(c => <option key={c}>{c}</option>)}</select></div>
                <div className="col-span-2"><label className="eos-overline">Body / rationale</label><textarea value={resForm.body} onChange={(e) => setResForm(f => ({ ...f, body: e.target.value }))} rows={3} className="mt-1 w-full px-2 py-2 text-sm border border-border rounded-md" data-testid="res-body"/></div>
                <div><label className="eos-overline">Effective from</label><input type="date" value={resForm.effective_from} onChange={(e) => setResForm(f => ({ ...f, effective_from: e.target.value }))} className="mt-1 w-full px-2 py-2 text-sm border border-border rounded-md" data-testid="res-eff"/></div>
              </div>
              <Button onClick={saveResolution} className="mt-3 bg-[hsl(244_53%_20%)] hover:bg-[hsl(244_53%_15%)]" data-testid="save-res-btn"><Plus size={14} className="mr-1.5"/> Record</Button>
            </div>
            <div className="eos-card">
              <div className="font-heading font-bold text-base mb-3">Versioned resolutions ({resolutions.length})</div>
              <div className="space-y-2">
                {resolutions.map(r => (
                  <div key={r.resolution_id} className="p-3 border border-border rounded-md" data-testid={`res-${r.resolution_id}`}>
                    <div className="flex items-center justify-between">
                      <div className="font-medium text-sm">{r.title}</div>
                      <span className="text-[10px] bg-secondary px-1.5 py-0.5 rounded font-mono-eos">v{r.version} · {r.council}</span>
                    </div>
                    <div className="text-[12px] text-muted-foreground mt-1">{r.body}</div>
                    <div className="text-[10px] text-muted-foreground mt-1">{new Date(r.created_at).toLocaleString()} · {r.created_by}</div>
                  </div>
                ))}
                {resolutions.length === 0 && <div className="text-sm text-muted-foreground italic">No resolutions yet.</div>}
              </div>
            </div>
          </div>
        )}

        {tab === "aggregate" && (
          <div className="space-y-4" data-testid="aggregate-pane">
            <div className="eos-card">
              <div className="font-heading font-bold text-base mb-3">Regulatory aggregation hub</div>
              <div className="text-[11px] text-muted-foreground mb-3">Pulls live data from SIS + LMS + Faculty + Scholarships + Audit Log into NIRF / NAAC / AISHE submission formats.</div>
              <div className="flex items-end gap-2">
                <div className="flex-1"><label className="eos-overline">Report</label><select value={aggCode} onChange={(e) => setAggCode(e.target.value)} className="mt-1 w-full px-2 py-2 text-sm border border-border rounded-md bg-white" data-testid="agg-select">{["NIRF", "NAAC", "AISHE"].map(c => <option key={c}>{c}</option>)}</select></div>
                <Button onClick={aggregateReport} className="bg-[hsl(20_90%_48%)] hover:bg-[hsl(20_90%_42%)]" data-testid="agg-run-btn"><Sparkle size={14} weight="fill" className="mr-1.5"/> Aggregate</Button>
              </div>
            </div>
            {aggReport && (
              <div className="eos-card" data-testid="agg-result">
                <div className="font-heading font-bold text-base mb-3">{aggReport.report} · {new Date(aggReport.generated_at).toLocaleString()}</div>
                <pre className="text-[11px] bg-secondary/50 p-3 rounded-md overflow-x-auto">{JSON.stringify(aggReport.indicators || aggReport.criteria || aggReport.data, null, 2)}</pre>
              </div>
            )}
          </div>
        )}
      </div>

      <CrudModal
        open={modal.open}
        onClose={close}
        title={modal.editing ? "Edit compliance report" : "New compliance report"}
        fields={buildFields(!!modal.editing)}
        initial={modal.editing || { status: "Draft", auto_pct: 0 }}
        onSubmit={save}
        submitLabel={modal.editing ? "Save changes" : "Create report"}
      />
    </div>
  );
}
